import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  WifiOff,
  RefreshCw,
  Clock,
  Server,
  Globe,
  MapPin,
  Eye
} from 'lucide-react';
import { enhancedSpacing } from '../../styles/enhanced-design-system';
import {
  productionAnimations
} from '../../styles/production-ui-system';
import { backendConnectivityService, ConnectivityDiagnostics } from '../../services/backendConnectivityService';
import { SystemStatusBadge } from '../common/StatusBadges';

const DiagnosticsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${enhancedSpacing[4]};
  width: 100%;
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${enhancedSpacing[4]};
`;

const StatusTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #F7F8FA;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;


const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${enhancedSpacing[4]};
`;

const ServiceCard = styled.div<{ status: 'connected' | 'offline' | 'error' }>`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  border: 1px solid ${props => {
    switch (props.status) {
      case 'connected': return 'rgba(34, 197, 94, 0.3)';
      case 'offline': return 'rgba(107, 114, 128, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  border-radius: 12px;
  padding: ${enhancedSpacing[4]};
  transition: all ${productionAnimations.duration.normal} ${productionAnimations.easing.smooth};
  position: relative;
  overflow: hidden;
  
  /* Status indicator glow */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => {
    switch (props.status) {
      case 'connected': return 'linear-gradient(90deg, #22C55E, #10B981)';
      case 'error': return 'linear-gradient(90deg, #EF4444, #DC2626)';
      default: return 'linear-gradient(90deg, #6B7280, #4B5563)';
    }
  }};
    box-shadow: 0 2px 8px ${props => {
    switch (props.status) {
      case 'connected': return 'rgba(34, 197, 94, 0.3)';
      case 'error': return 'rgba(239, 68, 68, 0.3)';
      default: return 'transparent';
    }
  }};
  }

  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04));
    transform: translateY(-2px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.15),
      0 0 0 1px ${props => {
    switch (props.status) {
      case 'connected': return 'rgba(34, 197, 94, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${enhancedSpacing[3]};
`;

const ServiceName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #F7F8FA;
`;

const ServiceStatus = styled.div<{ status: 'connected' | 'offline' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => {
    switch (props.status) {
      case 'connected': return '#22C55E';
      case 'offline': return '#9CA3AF';
      case 'error': return '#EF4444';
      default: return '#9CA3AF';
    }
  }};
`;

const ServiceMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #9CA3AF;
`;

const RecommendationsList = styled.div`
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  padding: ${enhancedSpacing[4]};
  margin-top: ${enhancedSpacing[4]};
`;

const RecommendationsTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #3B82F6;
  margin: 0 0 ${enhancedSpacing[3]} 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RecommendationItem = styled.div`
  font-size: 0.75rem;
  color: #D1D5DB;
  margin-bottom: 6px;
  padding-left: 16px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #3B82F6;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  color: #3B82F6;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LastUpdated = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #9CA3AF;
  margin-top: ${enhancedSpacing[4]};
  padding-top: ${enhancedSpacing[3]};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

function getServiceIcon(serviceName: string) {
  switch (serviceName.toLowerCase()) {
    case 'backend':
      return <Server size={16} />;
    case 'openweathermap':
      return <Globe size={16} />;
    case 'ip_geolocation':
      return <MapPin size={16} />;
    default:
      return <Activity size={16} />;
  }
}

function getStatusIcon(status: 'connected' | 'offline' | 'error') {
  switch (status) {
    case 'connected':
      return <CheckCircle size={12} />;
    case 'offline':
      return <WifiOff size={12} />;
    case 'error':
      return <AlertCircle size={12} />;
    default:
      return <WifiOff size={12} />;
  }
}

const SystemDiagnostics: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<ConnectivityDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const runDiagnostics = async () => {
    try {
      setLoading(true);
      const result = await backendConnectivityService.runComprehensiveDiagnostics();
      setDiagnostics(result);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to run diagnostics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runDiagnostics();

    // Run diagnostics every 2 minutes
    const interval = setInterval(runDiagnostics, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !diagnostics) {
    return (
      <DiagnosticsContainer>
        <StatusHeader>
          <StatusTitle>
            <Activity size={20} />
            System Diagnostics
          </StatusTitle>
          <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
        </StatusHeader>
        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '2rem 0' }}>
          Running system diagnostics...
        </div>
      </DiagnosticsContainer>
    );
  }

  if (!diagnostics) {
    return (
      <DiagnosticsContainer>
        <StatusHeader>
          <StatusTitle>
            <AlertCircle size={20} />
            System Diagnostics
          </StatusTitle>
          <SystemStatusBadge status="critical" />
        </StatusHeader>
        <div style={{ textAlign: 'center', color: '#EF4444', padding: '2rem 0' }}>
          Unable to run system diagnostics
        </div>
      </DiagnosticsContainer>
    );
  }

  // Calculate operational percentage
  const totalServices = 3; // Backend, OpenWeatherMap, IP Geolocation
  let operationalCount = 0;

  if (diagnostics.backend.reachable) operationalCount++;
  if (diagnostics.external_apis.openweathermap.reachable) operationalCount++;
  if (diagnostics.external_apis.ip_geolocation.reachable) operationalCount++;

  const operationalPercentage = Math.round((operationalCount / totalServices) * 100);

  // Only show healthy (green) if 100% operational
  let systemStatus: 'healthy' | 'degraded' | 'critical';
  if (operationalPercentage === 100) {
    systemStatus = 'healthy';
  } else if (operationalPercentage >= 50) {
    systemStatus = 'degraded';
  } else {
    systemStatus = 'critical';
  }

  return (
    <DiagnosticsContainer>
      <StatusHeader>
        <StatusTitle>
          <Activity size={20} />
          System Diagnostics
        </StatusTitle>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SystemStatusBadge status={systemStatus} percentage={operationalPercentage} />
          <RefreshButton onClick={runDiagnostics} disabled={loading}>
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </RefreshButton>
        </div>
      </StatusHeader>

      <ServicesGrid>
        {/* Backend Service */}
        <ServiceCard status={diagnostics.backend.reachable ? 'connected' : 'offline'}>
          <ServiceHeader>
            <ServiceName>
              {getServiceIcon('backend')}
              Alert Aid Backend
            </ServiceName>
            <ServiceStatus status={diagnostics.backend.reachable ? 'connected' : 'offline'}>
              {getStatusIcon(diagnostics.backend.reachable ? 'connected' : 'offline')}
              {diagnostics.backend.reachable ? 'Online' : 'Offline'}
            </ServiceStatus>
          </ServiceHeader>
          <ServiceMetrics>
            <MetricRow>
              <span>Response Time:</span>
              <span>{diagnostics.backend.response_time}ms</span>
            </MetricRow>
            <MetricRow>
              <span>Endpoints Available:</span>
              <span>
                {Object.values(diagnostics.backend.endpoints).filter(e => e.available).length}/
                {Object.keys(diagnostics.backend.endpoints).length}
              </span>
            </MetricRow>
          </ServiceMetrics>
        </ServiceCard>

        {/* OpenWeatherMap API */}
        <ServiceCard status={diagnostics.external_apis.openweathermap.reachable ? 'connected' : 'offline'}>
          <ServiceHeader>
            <ServiceName>
              {getServiceIcon('openweathermap')}
              OpenWeatherMap
            </ServiceName>
            <ServiceStatus status={diagnostics.external_apis.openweathermap.reachable ? 'connected' : 'offline'}>
              {getStatusIcon(diagnostics.external_apis.openweathermap.reachable ? 'connected' : 'offline')}
              {diagnostics.external_apis.openweathermap.reachable ? 'Online' : 'Offline'}
            </ServiceStatus>
          </ServiceHeader>
          <ServiceMetrics>
            <MetricRow>
              <span>Response Time:</span>
              <span>{diagnostics.external_apis.openweathermap.response_time}ms</span>
            </MetricRow>
            <MetricRow>
              <span>Status:</span>
              <span>{diagnostics.external_apis.openweathermap.status}</span>
            </MetricRow>
          </ServiceMetrics>
        </ServiceCard>

        {/* IP Geolocation API */}
        <ServiceCard status={diagnostics.external_apis.ip_geolocation.reachable ? 'connected' : 'offline'}>
          <ServiceHeader>
            <ServiceName>
              {getServiceIcon('ip_geolocation')}
              IP Geolocation
            </ServiceName>
            <ServiceStatus status={diagnostics.external_apis.ip_geolocation.reachable ? 'connected' : 'offline'}>
              {getStatusIcon(diagnostics.external_apis.ip_geolocation.reachable ? 'connected' : 'offline')}
              {diagnostics.external_apis.ip_geolocation.reachable ? 'Online' : 'Offline'}
            </ServiceStatus>
          </ServiceHeader>
          <ServiceMetrics>
            <MetricRow>
              <span>Response Time:</span>
              <span>{diagnostics.external_apis.ip_geolocation.response_time}ms</span>
            </MetricRow>
            <MetricRow>
              <span>Status:</span>
              <span>{diagnostics.external_apis.ip_geolocation.status}</span>
            </MetricRow>
          </ServiceMetrics>
        </ServiceCard>
      </ServicesGrid>

      {diagnostics.recommendations.length > 0 && (
        <RecommendationsList>
          <RecommendationsTitle>
            <Eye size={14} />
            System Recommendations
          </RecommendationsTitle>
          {diagnostics.recommendations.map((recommendation, index) => (
            <RecommendationItem key={index}>
              {recommendation}
            </RecommendationItem>
          ))}
        </RecommendationsList>
      )}

      <LastUpdated>
        <Clock size={12} />
        Last checked: {lastUpdate.toLocaleTimeString()}
      </LastUpdated>
    </DiagnosticsContainer>
  );
};

export { SystemDiagnostics };
export default SystemDiagnostics;
